import { Code, Text } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { AnimatePresence } from "motion/react";
import { motion } from "motion/react";

import RetryIcon from "~icons/heroicons/arrow-uturn-left-20-solid";
import SwitchPerspectiveIcon from "~icons/heroicons/arrows-right-left-20-solid";
import ChatIcon from "~icons/heroicons/chat-bubble-left-right-20-solid";

import AppLayout from "~/components/AppLayout";
import JoinTheClub from "~/components/JoinTheClub";
import PatternsLinePlayer from "~/components/PatternsLinePlayer";
import PatternsStoryPlayer from "~/components/PatternsStoryPlayer";
import PatternsTexting from "~/components/PatternsTexting";
import { type PatternsConfig } from "~/types";

export interface PatternsDemoPageProps extends SharedPageProps {
  config: PatternsConfig;
  initialPhase: string | null;
}

const MotionGroup = motion.create(Group);

const PatternsDemoPage: PageComponent<PatternsDemoPageProps> = ({
  config,
  initialPhase,
}) => {
  // == Cyclebreaking scenario
  const [cyclebreakingScenarioIndex, setCyclebreakingScenarioIndex] =
    useState(0);
  const nextCyclebreakingScenarioIndex =
    (cyclebreakingScenarioIndex + 1) % config.cyclebreaking_scenarios.length;
  const cyclebreakingPlayableScenario =
    config.cyclebreaking_scenarios[cyclebreakingScenarioIndex];
  invariant(
    cyclebreakingPlayableScenario,
    "Invalid cyclebreaking scenario index",
  );
  const [cyclebreakingScore, setCyclebreakingScore] = useState(0);

  // == Clear scene and set phase
  const [phase, setPhase] = useState<string>(initialPhase ?? "initial_setting");
  const [clearScene, setClearScene] = useState(false);
  useEffect(() => {
    if (clearScene) {
      const timeout = setTimeout(() => {
        setClearScene(false);
      }, 1000);
      return () => {
        clearInterval(timeout);
      };
    }
  }, [clearScene]);
  const clearSceneAndSetPhase = useCallback(
    (phase: string) => {
      setClearScene(true);
      setPhase(phase);
    },
    [setPhase],
  );

  // == Render phase
  const renderPhase = useCallback(
    (phase: string) => {
      switch (phase) {
        case "initial_setting": {
          return (
            <PatternsLinePlayer
              lines={config.initial_scenario.setting_lines}
              followOn={
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: 0.5,
                    type: "spring",
                    stiffness: 50,
                    damping: 15,
                    duration: 0.5,
                  }}
                  mx="auto"
                >
                  <Button
                    leftSection={<PhoneIcon />}
                    variant="white"
                    onClick={() => {
                      clearSceneAndSetPhase("initial_texting");
                    }}
                  >
                    Pick up the phone
                  </Button>
                </Box>
              }
            />
          );
        }
        case "initial_texting": {
          return (
            <PatternsTexting
              events={config.initial_scenario.texting_events}
              followOn={
                <PatternsLinePlayer
                  delay={1400}
                  lines={[
                    <>
                      A negative pattern is a{" "}
                      <span style={{ fontWeight: 700 }}>habit</span> in your
                      relationship that keeps causing problems.
                    </>,
                    "Patterns are formed from our core beliefs.",
                    "How do you discover core beliefs?",
                    "You need to listen.",
                  ]}
                  followOn={
                    <PatternsStoryPlayer
                      storyUrl={config.story_url}
                      onAnimationStart={scrollToBottom}
                      onCompleted={() => {
                        clearSceneAndSetPhase(
                          "cyclebreaking_perspective_selection",
                        );
                      }}
                    />
                    // <Button
                    //   leftSection={<PhoneIcon />}
                    //   variant="white"
                    //   display="block"
                    //   mx="auto"
                    //   onClick={() => {
                    //     clearSceneAndSetPhase("cyclebreaking_setting");
                    //   }}
                    // >
                    //   What can Red do to fix this?
                    // </Button>
                  }
                />
              }
              // continueButtonLabel="What can Red do to fix this?"
              // onContinue={() => {
              //   clearSceneAndSetPhase("cyclebreaking_setting");
              // }}
            />
          );
        }
        case "cyclebreaking_perspective_selection": {
          return (
            <PatternsLinePlayer
              lines={[
                "Amber gets louder because she's afraid of being ignored, which overwhelmed Red.",
                "Red shuts down to avoid fighting, but his silence makes Amber feel abandoned.",
                "Now that you understand the pattern, let’s see if you can break it.",
              ]}
              followOn={
                <MotionGroup
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ ease: "easeIn", duration: 0.4 }}
                  justify="center"
                >
                  {config.cyclebreaking_scenarios.map((playableScenario, i) => (
                    <Button
                      key={playableScenario.perspective}
                      leftSection={<ChatIcon />}
                      variant="white"
                      size="md"
                      onClick={() => {
                        setCyclebreakingScenarioIndex(i);
                        clearSceneAndSetPhase("cyclebreaking_setting");
                      }}
                    >
                      Play as {playableScenario.perspective}
                    </Button>
                  ))}
                </MotionGroup>
              }
            />
          );
        }
        case "cyclebreaking_setting": {
          invariant(
            cyclebreakingPlayableScenario,
            "Missing cyclebreaking scenario",
          );
          return (
            <PatternsLinePlayer
              lines={cyclebreakingPlayableScenario.scenario.setting_lines}
              followOn={
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: 0.5,
                    type: "spring",
                    stiffness: 50,
                    damping: 15,
                    duration: 0.5,
                  }}
                  mx="auto"
                >
                  <Button
                    leftSection={<PhoneIcon />}
                    variant="white"
                    onClick={() => {
                      clearSceneAndSetPhase("cyclebreaking_texting");
                    }}
                  >
                    Pick up the phone
                  </Button>
                </Box>
              }
            />
          );
        }
        case "cyclebreaking_texting": {
          invariant(
            cyclebreakingPlayableScenario,
            "Missing cyclebreaking scenario",
          );
          return (
            <PatternsTexting
              events={cyclebreakingPlayableScenario.scenario.texting_events}
              followOn={
                <Card
                  component={motion.div}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 1,
                      type: "spring",
                      stiffness: 50,
                      damping: 15,
                      duration: 0.5,
                    },
                  }}
                  onAnimationStart={scrollToBottom}
                  withBorder
                >
                  <Stack gap="xs">
                    <Text lh="xs">
                      {cyclebreakingScore > 0 ? (
                        <>
                          You broke the cycle{" "}
                          <span style={{ fontWeight: 700 }}>
                            {cyclebreakingScore}{" "}
                            {inflect("times", cyclebreakingScore)}
                          </span>{" "}
                          by{" "}
                          {cyclebreakingPlayableScenario.cyclebreaking_method}
                        </>
                      ) : (
                        <>
                          The anxious-avoidant trap is powerful, but you can
                          break free.
                        </>
                      )}
                    </Text>
                    <Group gap="xs" wrap="wrap" justify="center">
                      <Button
                        variant="white"
                        size="md"
                        leftSection={<SwitchPerspectiveIcon />}
                        onClick={() => {
                          setCyclebreakingScore(0);
                          setCyclebreakingScenarioIndex(
                            nextCyclebreakingScenarioIndex,
                          );
                          clearSceneAndSetPhase("cyclebreaking_texting");
                        }}
                      >
                        Play as{" "}
                        {
                          config.cyclebreaking_scenarios[
                            nextCyclebreakingScenarioIndex
                          ]!.perspective
                        }
                      </Button>
                      <Button
                        variant="white"
                        size="md"
                        leftSection={<RetryIcon />}
                        onClick={() => {
                          setCyclebreakingScore(0);
                          clearSceneAndSetPhase("cyclebreaking_texting");
                        }}
                      >
                        Play again
                      </Button>
                      <Button
                        variant="white"
                        size="md"
                        rightSection={<ContinueIcon />}
                        onClick={() => {
                          clearSceneAndSetPhase("join_the_club");
                        }}
                      >
                        Continue
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              }
              onResponseScoreChange={setCyclebreakingScore}
            />
          );
        }
        case "join_the_club":
          return <JoinTheClub headline="Curious about your own patterns?" />;
        default:
          return <Code block>Unknown phase: {phase}</Code>;
      }
    },
    [cyclebreakingScenarioIndex, cyclebreakingScore], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // == Tracking
  const [demoStarted, setDemoStarted] = useLocalStorage<boolean | undefined>({
    key: "patterns_started",
    defaultValue: false,
    getInitialValueInEffect: false,
  });
  useEffect(() => {
    if (demoStarted === undefined) {
      return;
    }
    if (
      (phase === "initial_setting" && !demoStarted) ||
      phase === "join_the_club"
    ) {
      const type = phase === "initial_setting" ? "started" : "finished";
      void routes.demoEvents
        .create({
          data: {
            demo_event: {
              demo_id: "patterns",
              type,
            },
          },
        })
        .then(() => {
          setDemoStarted(type === "started");
        });
    }
  }, [phase, demoStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AnimatePresence>
      <motion.div
        key={phase}
        initial={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.25 } }}
      >
        {!clearScene && renderPhase(phase)}
      </motion.div>
    </AnimatePresence>
  );
};

PatternsDemoPage.layout = page => (
  <AppLayout<PatternsDemoPageProps>
    title="Patterns"
    withContainer
    containerSize="xs"
    withGutter
  >
    {page}
  </AppLayout>
);

export default PatternsDemoPage;
